import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "../components/layout/AppShell";
import { Badge, Card, Spinner } from "../components/common/UIKit";
import SearchableDropdown from "../components/common/SearchableDropdown";
import { useAuthStore } from "../store/authStore";
import { listAdminAgents, getAgentSubscriptions, swapCounty, adminCancelLeadType } from "../api/admin";
import { listCounties } from "../api/counties";
import { getSettings } from "../api/settings";
import { readPricing, formatPrice, getLeadTypePrice, calcLeadTypeTotal } from "../utils/pricing";
import { Search, Pencil, X, Check, Plus, ChevronLeft, ChevronRight, Users } from "lucide-react";
import ConfirmModal from "../components/common/ConfirmModal";
import { errMsg } from "../lib/utils";

function calcSubtotal(name, count, pricing) {
  return calcLeadTypeTotal(name, count, pricing);
}

function CountyRow({ sub, idx, ltName, allSubs, countyOptions, onSwap, pricePerCounty }) {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const usedIds = new Set(allSubs.filter((s) => s.lead_type_name === ltName).map((s) => s.county_id));
  const available = countyOptions.filter((c) => !usedIds.has(c.value) || c.value === sub.county_id);

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await onSwap(ltName, sub.county_id, selected);
      setSaved(true);
      setEditing(false);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-between gap-2 rounded-md border border-zinc-200 px-3 py-2" data-testid={`admin-sub-${sub.id}`}>
      <div className="flex-1 min-w-0">
        {editing ? (
          <SearchableDropdown
            testId={`admin-swap-county-${sub.id}`}
            value={selected}
            onChange={setSelected}
            options={available}
            placeholder="Select replacement county"
            searchPlaceholder="Search counties..."
            clearable={false}
          />
        ) : (
          <div className="flex items-center gap-2 min-w-0">
            <p className="text-sm font-bold text-zinc-950 truncate">{sub.county_name}</p>
            {sub.state && <span className="text-[10px] font-bold uppercase tracking-wide text-zinc-400 flex-shrink-0">{sub.state}</span>}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {idx === 0
          ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">INCLUDED</span>
          : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">+{formatPrice(pricePerCounty)}/MO</span>
        }
        {saved && <span className="text-xs font-semibold text-green-600">Updated</span>}
        {editing ? (
          <>
            <button type="button" onClick={handleSave} disabled={!selected || saving} className="p-1 rounded text-green-600 hover:bg-green-50 disabled:opacity-40" data-testid={`admin-save-swap-${sub.id}`}>
              {saving ? <Spinner className="h-3.5 w-3.5" /> : <Check className="h-3.5 w-3.5" />}
            </button>
            <button type="button" onClick={() => { setEditing(false); setSelected(""); }} className="p-1 rounded text-zinc-400 hover:text-zinc-950">
              <X className="h-3.5 w-3.5" />
            </button>
          </>
        ) : (
          <button type="button" onClick={() => setEditing(true)} className="p-1 rounded text-zinc-400 hover:text-zinc-950 hover:bg-zinc-50" data-testid={`admin-edit-county-${sub.id}`}>
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

function AddCountyRow({ ltName, allSubs, countyOptions, onAdd }) {
  const [selected, setSelected] = useState("");
  const [saving, setSaving] = useState(false);

  const usedIds = new Set(allSubs.filter((s) => s.lead_type_name === ltName).map((s) => s.county_id));
  const available = countyOptions.filter((c) => !usedIds.has(c.value));

  const handleAdd = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await onAdd(ltName, selected);
      setSelected("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2 mt-2">
      <div className="flex-1">
        <SearchableDropdown
          testId={`admin-add-county-${ltName}`}
          value={selected}
          onChange={setSelected}
          options={available}
          placeholder="Add county..."
          searchPlaceholder="Search counties..."
          clearable={false}
        />
      </div>
      <button
        type="button"
        onClick={handleAdd}
        disabled={!selected || saving}
        className="h-9 px-3 rounded-md bg-zinc-950 text-white text-xs font-semibold hover:bg-zinc-800 disabled:opacity-40 flex-shrink-0"
        data-testid={`admin-add-county-save-${ltName}`}
      >
        {saving ? <Spinner className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}

function AgentDetail({ agentId, agentMeta, pricing, onRefresh }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countyOptions, setCountyOptions] = useState([]);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelWhen, setCancelWhen] = useState("immediately");
  const [cancelling, setCancelling] = useState(false);
  const { pricePerCounty } = pricing;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAgentSubscriptions(agentId);
      setDetail(data);
      const stateCode = data.subscriptions[0]?.state_code;
      if (stateCode) {
        const resp = await listCounties({ state_code: stateCode });
        setCountyOptions((resp.items || []).map((c) => ({ value: c.id, label: c.name })));
      }
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  useEffect(() => { load(); }, [load]);

  const byLeadType = useMemo(() => {
    if (!detail) return [];
    const map = new Map();
    for (const s of detail.subscriptions) {
      const name = s.lead_type_name;
      if (!name) continue;
      if (!map.has(name)) map.set(name, { name, label: s.lead_type_label || name, counties: [] });
      map.get(name).counties.push(s);
    }
    return Array.from(map.values()).map((row) => ({
      ...row,
      counties: row.counties.sort((a, b) => (a.county_name || "").localeCompare(b.county_name || "")),
    }));
  }, [detail]);

  const overallTotal = useMemo(
    () => byLeadType.reduce((sum, row) => sum + calcSubtotal(row.name, row.counties.length, pricing), 0),
    [byLeadType, pricing]
  );

  const memberSince = agentMeta?.created_at
    ? new Date(agentMeta.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })
    : null;

  const handleSwap = async (ltName, oldCountyId, newCountyId) => {
    await swapCounty(agentId, { lead_type: ltName, old_county_id: oldCountyId, new_county_id: newCountyId });
    toast.success("County updated");
    await load();
  };


  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await adminCancelLeadType(agentId, { lead_type: cancelTarget.name, when: cancelWhen });
      toast.success(`${cancelTarget.label} ${cancelWhen === "immediately" ? "cancelled" : "scheduled for cancellation"}`);
      setCancelTarget(null);
      await load();
      onRefresh();
    } catch (e) {
      toast.error(errMsg(e, "Could not cancel"));
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Spinner /></div>;
  if (!detail) return null;

  return (
    <div className="flex flex-col h-full" data-testid="admin-agent-detail">
      <div className="flex-1 overflow-y-auto p-6 pb-24">
        {/* Agent header */}
        <div className="mb-6 pb-5 border-b border-zinc-200">
          <h2 className="text-xl font-black tracking-tight text-zinc-950 mb-0.5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {detail.agent_name}
          </h2>
          <p className="text-sm text-zinc-500 mb-2">{detail.agent_email}</p>
          <div className="flex flex-wrap items-center gap-2 text-xs text-zinc-500">
            <span className="font-semibold text-zinc-700">{byLeadType.length} active lead type{byLeadType.length !== 1 ? "s" : ""}</span>
            {byLeadType.length > 0 && <><span className="text-zinc-300">·</span><span className="font-semibold text-zinc-700">{formatPrice(overallTotal)}/mo total</span></>}
            {memberSince && <><span className="text-zinc-300">·</span><span>Member since {memberSince}</span></>}
          </div>
        </div>

        {byLeadType.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center" data-testid="admin-no-subs">
            <div className="h-12 w-12 rounded-full bg-zinc-100 flex items-center justify-center mb-3">
              <Users className="h-5 w-5 text-zinc-400" />
            </div>
            <p className="text-sm font-semibold text-zinc-950 mb-1">No active subscriptions</p>
            <p className="text-xs text-zinc-500">This agent has not subscribed to any lead types yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {byLeadType.map((row) => {
              const subtotal = calcSubtotal(row.name, row.counties.length, pricing);
              return (
                <Card key={row.name} data-testid={`admin-lt-card-${row.name}`}>
                  <div className="flex items-start justify-between gap-4 mb-3">
                    <div>
                      <h3 className="text-base font-bold text-zinc-950">{row.label}</h3>
                      <p className="text-xs text-zinc-500">{row.counties.length} {row.counties.length === 1 ? "county" : "counties"}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-sm font-bold text-zinc-950">{formatPrice(subtotal)}<span className="text-xs font-normal text-zinc-400">/mo</span></p>
                      <button
                        type="button"
                        onClick={() => { setCancelTarget(row); setCancelWhen("immediately"); }}
                        className="text-xs font-semibold text-red-600 hover:underline"
                        data-testid={`admin-cancel-lt-${row.name}`}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {row.counties.map((sub, idx) => (
                      <CountyRow
                        key={sub.id}
                        sub={sub}
                        idx={idx}
                        ltName={row.name}
                        allSubs={detail.subscriptions}
                        countyOptions={countyOptions}
                        onSwap={handleSwap}
                        pricePerCounty={pricePerCounty}
                      />
                    ))}
                  </div>
                  
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Sticky footer total */}
      {byLeadType.length > 0 && (
        <div className="absolute bottom-0 left-80 right-0 border-t border-zinc-200 bg-white px-6 py-3 flex items-center justify-between" data-testid="admin-total-footer">
          <div className="flex items-center gap-3 text-xs text-zinc-500">
            <span className="font-bold uppercase tracking-widest">Overall Total</span>
            <span className="text-zinc-300">·</span>
            <span>{byLeadType.length} lead type{byLeadType.length !== 1 ? "s" : ""}</span>
          </div>
          <span className="text-xl font-black text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            {formatPrice(overallTotal)}<span className="text-sm font-normal text-zinc-400">/mo</span>
          </span>
        </div>
      )}

      {/* Cancel modal */}
      {cancelTarget && (
        <ConfirmModal
          title={`Cancel ${cancelTarget.label} — ${detail.agent_name}`}
          message={
            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest text-zinc-500">When should this take effect?</p>
              {[
                { value: "immediately", label: "Cancel immediately" },
                { value: "end_of_period", label: "Cancel at end of billing period" },
              ].map((opt) => (
                <label key={opt.value} className="flex items-center gap-2 cursor-pointer text-sm text-zinc-700">
                  <input type="radio" name="cancel-when" value={opt.value} checked={cancelWhen === opt.value} onChange={() => setCancelWhen(opt.value)} className="accent-zinc-950" />
                  {opt.label}
                </label>
              ))}
            </div>
          }
          confirmLabel={cancelling ? "Cancelling..." : "Confirm cancel"}
          cancelLabel="Keep subscription"
          confirmVariant="danger"
          onConfirm={handleCancelConfirm}
          onCancel={() => setCancelTarget(null)}
        />
      )}
    </div>
  );
}

export default function AdminSubscriptionsPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [settings, setSettings] = useState(null);
  const [agents, setAgents] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [selectedAgentId, setSelectedAgentId] = useState(null);
  const [selectedAgentMeta, setSelectedAgentMeta] = useState(null);
  const [detailKey, setDetailKey] = useState(0);
  const PAGE_SIZE = 20;

  useEffect(() => {
    if (!user?.is_admin) { navigate("/dashboard", { replace: true }); return; }
    getSettings().then(setSettings).catch(() => {});
  }, [user, navigate]);

  const loadAgents = useCallback(async () => {
    setLoadingAgents(true);
    try {
      const data = await listAdminAgents({ search: search || undefined, page, page_size: PAGE_SIZE });
      setAgents(data.results);
      setTotal(data.total);
    } finally {
      setLoadingAgents(false);
    }
  }, [search, page]);

  useEffect(() => { loadAgents(); }, [loadAgents]);

  const pricing = useMemo(() => readPricing(settings || {}), [settings]);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  if (!user?.is_admin) return null;

  return (
    <AppShell title="Manage Subscriptions" subtitle="Admin — view and edit agent subscriptions">
      <div className="flex relative" style={{ height: "calc(100vh - 120px)" }}>

        {/* Left panel */}
        <div className="w-80 flex-shrink-0 border-r border-zinc-200 flex flex-col" data-testid="admin-agent-list">
          <div className="p-4 border-b border-zinc-200">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search agents..."
                className="w-full h-9 pl-8 pr-3 text-sm border border-zinc-200 rounded-md focus:outline-none focus:border-zinc-950"
                data-testid="admin-agent-search"
              />
            </div>
            <p className="text-xs text-zinc-400 mt-2">{total} agent{total !== 1 ? "s" : ""}</p>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loadingAgents ? (
              <div className="flex items-center justify-center py-12"><Spinner /></div>
            ) : agents.length === 0 ? (
              <p className="text-sm text-zinc-500 text-center py-8">No agents found</p>
            ) : (
              agents.map((agent) => {
                const active = selectedAgentId === agent.id;
                return (
                  <button
                    key={agent.id}
                    type="button"
                    onClick={() => { setSelectedAgentId(agent.id); setSelectedAgentMeta(agent); setDetailKey((k) => k + 1); }}
                    className={`w-full text-left px-4 py-3 border-b border-zinc-100 transition-colors ${active ? "bg-zinc-950" : "hover:bg-zinc-50"}`}
                    data-testid={`admin-agent-row-${agent.id}`}
                  >
                    <p className={`text-sm font-semibold truncate ${active ? "text-white" : "text-zinc-950"}`}>{agent.full_name}</p>
                    <p className={`text-xs truncate ${active ? "text-zinc-300" : "text-zinc-500"}`}>{agent.email}</p>
                    <p className={`text-xs mt-0.5 ${active ? "text-zinc-400" : "text-zinc-400"}`}>
                      {agent.active_lead_types} lead type{agent.active_lead_types !== 1 ? "s" : ""}
                    </p>
                  </button>
                );
              })
            )}
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-200 text-xs text-zinc-500">
            <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-zinc-100 disabled:opacity-40">
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <span>{page} / {totalPages}</span>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-zinc-100 disabled:opacity-40">
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Right panel */}
        <div className="flex-1 min-w-0 relative overflow-hidden" data-testid="admin-agent-detail-panel">
          {selectedAgentId ? (
            <AgentDetail
              key={detailKey}
              agentId={selectedAgentId}
              agentMeta={selectedAgentMeta}
              pricing={pricing}
              onRefresh={loadAgents}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center gap-2">
              <div className="h-12 w-12 rounded-full bg-zinc-100 flex items-center justify-center">
                <Users className="h-5 w-5 text-zinc-400" />
              </div>
              <p className="text-sm font-semibold text-zinc-950">Select an agent</p>
              <p className="text-xs text-zinc-500">Click an agent on the left to view their subscriptions</p>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
